import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  getDataset,
  getProfile,
  listVersions,
  recomputeProfile,
  type Dataset,
  type DatasetProfile,
  type DatasetVersion,
} from '../api/datasets';
import { CompletenessBar, StatTile } from '../components/charts';
import { ColumnProfileCard } from '../components/datasets/ColumnProfileCard';
import { SuggestionList } from '../components/datasets/SuggestionList';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, Card, ErrorState, LoadingState } from '../components/ui';
import { usePollWhile } from '../hooks/usePollWhile';
import './DatasetProfilePage.css';

type State = 'loading' | 'ready' | 'error';

/** `/datasets/:id/profile` — what is actually in this file. */
export function DatasetProfilePage() {
  const { datasetId = '' } = useParams();

  const [state, setState] = useState<State>('loading');
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [version, setVersion] = useState<DatasetVersion | null>(null);
  const [profile, setProfile] = useState<DatasetProfile | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const [loadedDataset, versions] = await Promise.all([
        getDataset(datasetId),
        listVersions(datasetId),
      ]);
      setDataset(loadedDataset);

      const latest = versions[0] ?? null;
      setVersion(latest);
      setProfile(latest?.status === 'READY' ? await getProfile(datasetId, latest.id) : null);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [datasetId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The profile is computed by a worker, like the ingestion it follows.
  usePollWhile(profile?.status === 'PENDING' || version?.status === 'PROCESSING', load);

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Reading your data…" />
      </AppShell>
    );
  }

  if (state === 'error' || !dataset) {
    return (
      <AppShell>
        <ErrorState
          title="This dataset is not available"
          description={error?.message ?? 'It may have been deleted, or it is not yours.'}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>{dataset.name}</h1>
        <p>
          What is in this file · <Link to={`/datasets/${datasetId}`}>back to the dataset</Link>
        </p>
      </div>
      {dataset.is_mine && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (version) {
              void recomputeProfile(datasetId, version.id).then(() => void load());
            }
          }}
        >
          Recompute
        </Button>
      )}
    </div>
  );

  if (!profile || profile.status !== 'READY') {
    return (
      <AppShell header={header}>
        {profile?.status === 'FAILED' ? (
          <Alert tone="warning" title="The profile could not be computed">
            Your data is stored and unaffected — only these statistics are missing. Try recomputing;
            if it keeps failing, the file may hold something unusual.
          </Alert>
        ) : (
          <LoadingState label="Measuring your data…" />
        )}
      </AppShell>
    );
  }

  const quality = [
    profile.duplicate_row_count > 0 &&
      `${profile.duplicate_row_count.toLocaleString()} duplicate row${
        profile.duplicate_row_count === 1 ? '' : 's'
      }`,
    profile.empty_column_count > 0 && `${profile.empty_column_count} empty column(s)`,
    profile.constant_column_count > 0 &&
      `${profile.constant_column_count} column(s) with a single value`,
  ].filter(Boolean) as string[];

  return (
    <AppShell header={header}>
      <Card title="Overview" description="The shape of this dataset at a glance.">
        <div className="sc-profile__stats">
          <StatTile label="Rows" value={profile.row_count.toLocaleString()} />
          <StatTile label="Columns" value={profile.column_count} />
          <StatTile
            label="Duplicate rows"
            value={profile.duplicate_row_count.toLocaleString()}
            detail={`${profile.duplicate_row_percentage}% of rows`}
            tone={profile.duplicate_row_count > 0 ? 'warning' : 'default'}
          />
          <StatTile
            label="Missing cells"
            value={profile.missing_cell_count.toLocaleString()}
            detail={`${profile.missing_cell_percentage}% of cells`}
            tone={profile.missing_cell_percentage > 20 ? 'warning' : 'default'}
          />
        </div>

        <div className="sc-profile__completeness">
          <CompletenessBar
            percentage={profile.completeness_percentage}
            label="Cells with a value"
          />
        </div>

        {quality.length > 0 && (
          <Alert tone="warning" title="Worth a look before you analyse">
            <ul className="sc-profile__quality">
              {quality.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="sc-profile__note">
              None of this is necessarily wrong — repeated rows and single-value columns are normal
              in some data. It is only worth knowing before you draw conclusions.
            </p>
          </Alert>
        )}
      </Card>

      {profile.suggestions.length > 0 && (
        <Card
          title="Where to look next"
          description="Questions this data can answer, proposed from what the profile measured."
        >
          <SuggestionList suggestions={profile.suggestions} />
          <p className="sc-profile__note">
            These are places to look, not conclusions. Simple Coffee does not know what your columns
            mean — you decide whether a question is worth asking.
          </p>
        </Card>
      )}

      {profile.relationships.length > 0 && (
        <Card
          title="Possible relationships"
          description="Columns that look like they line up with your other datasets."
        >
          <ul className="sc-relationships">
            {profile.relationships.map((candidate) => (
              <li
                key={`${candidate.column}-${candidate.related_version_id}-${candidate.related_column}`}
              >
                <div>
                  <code>{candidate.column}</code> may match <code>{candidate.related_column}</code>{' '}
                  in{' '}
                  <Link to={`/datasets/${candidate.related_dataset_id}`}>
                    {candidate.related_dataset_name}
                  </Link>
                </div>
                <div className="sc-relationships__meta sc-numeric">
                  {candidate.overlap_percentage}% of values matched · {candidate.kind} ·{' '}
                  {candidate.confidence} signal
                </div>
              </li>
            ))}
          </ul>
          <p className="sc-profile__note">
            These are suggestions based on overlapping values, not confirmed joins. You decide
            whether two columns really mean the same thing.
          </p>
        </Card>
      )}

      <Card title="Columns" description={`All ${profile.column_count} columns, in file order.`}>
        <div className="sc-profile__columns">
          {profile.columns.map((column) => (
            <ColumnProfileCard key={column.name} column={column} />
          ))}
        </div>
      </Card>
    </AppShell>
  );
}
